import {
	createPaymentMethod,
	getStripe,
	handleStripeError,
} from "@/utils/stripe";
import { CreditCardOutlined, LockOutlined } from "@ant-design/icons";
import {
	CardElement,
	Elements,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import {
	Button,
	Card,
	Col,
	Form,
	Input,
	Row,
	Space,
	Typography,
	message,
} from "antd";
import React, { useState } from "react";

const { Title, Text } = Typography;

interface StripePaymentFormProps {
	onPaymentMethodCreated: (paymentMethodId: string) => void;
	onCancel: () => void;
	loading?: boolean;
	title?: string;
	submitText?: string;
}

const PaymentFormContent: React.FC<StripePaymentFormProps> = ({
	onPaymentMethodCreated,
	onCancel,
	loading = false,
	title = "Add Payment Method",
	submitText = "Add Payment Method",
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const [form] = Form.useForm();
	const [processing, setProcessing] = useState(false);

	const cardElementOptions = {
		style: {
			base: {
				fontSize: "16px",
				color: "#424770",
				"::placeholder": {
					color: "#aab7c4",
				},
				padding: "12px",
			},
			invalid: {
				color: "#9e2146",
			},
		},
		hidePostalCode: true, // We'll collect this separately
	};

	const handleSubmit = async (values: any) => {
		if (!stripe || !elements) {
			message.error("Stripe has not loaded yet. Please try again.");
			return;
		}

		setProcessing(true);

		try {
			const billingDetails = {
				name: values.name,
				email: values.email,
				address: {
					line1: values.address1,
					line2: values.address2,
					city: values.city,
					state: values.state,
					postal_code: values.postalCode,
					country: values.country || "US",
				},
			};

			const { error, paymentMethod } = await createPaymentMethod(
				stripe,
				elements,
				billingDetails,
			);

			if (error) {
				message.error(handleStripeError(error));
				return;
			}

			if (paymentMethod) {
				message.success("Payment method added successfully");
				onPaymentMethodCreated(paymentMethod.id);
			}
		} catch (error) {
			console.error("Error creating payment method:", error);
			message.error("Failed to add payment method. Please try again.");
		} finally {
			setProcessing(false);
		}
	};

	return (
		<Card>
			<Title level={4}>
				<Space>
					<CreditCardOutlined />
					{title}
				</Space>
			</Title>

			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				disabled={processing || loading}
			>
				{/* Card Information */}
				<Form.Item label="Card Information" required>
					<div
						style={{
							border: "1px solid #d9d9d9",
							borderRadius: "6px",
							padding: "12px",
							backgroundColor: "#fff",
						}}
					>
						<CardElement options={cardElementOptions} />
					</div>
				</Form.Item>

				{/* Cardholder Name */}
				<Form.Item
					name="name"
					label="Cardholder Name"
					rules={[
						{ required: true, message: "Please enter the cardholder name" },
					]}
				>
					<Input placeholder="John Doe" />
				</Form.Item>

				{/* Email */}
				<Form.Item
					name="email"
					label="Email"
					rules={[
						{ required: true, message: "Please enter your email" },
						{ type: "email", message: "Please enter a valid email" },
					]}
				>
					<Input placeholder="john@example.com" />
				</Form.Item>

				{/* Billing Address */}
				<Title level={5}>Billing Address</Title>

				<Form.Item
					name="address1"
					label="Address Line 1"
					rules={[{ required: true, message: "Please enter your address" }]}
				>
					<Input placeholder="123 Main Street" />
				</Form.Item>

				<Form.Item name="address2" label="Address Line 2 (Optional)">
					<Input placeholder="Apartment, suite, etc." />
				</Form.Item>

				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<Form.Item
							name="city"
							label="City"
							rules={[{ required: true, message: "Please enter your city" }]}
						>
							<Input placeholder="San Francisco" />
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item
							name="state"
							label="State"
							rules={[{ required: true, message: "Please enter your state" }]}
						>
							<Input placeholder="CA" />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<Form.Item
							name="postalCode"
							label="Postal Code"
							rules={[
								{ required: true, message: "Please enter your postal code" },
							]}
						>
							<Input placeholder="94105" />
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item name="country" label="Country" initialValue="US">
							<Input placeholder="US" />
						</Form.Item>
					</Col>
				</Row>

				{/* Security Notice */}
				<div
					style={{
						backgroundColor: "#f6ffed",
						border: "1px solid #b7eb8f",
						borderRadius: "6px",
						padding: "12px",
						marginBottom: "24px",
					}}
				>
					<Space>
						<LockOutlined style={{ color: "#52c41a" }} />
						<Text type="secondary" style={{ fontSize: "12px" }}>
							Your payment information is encrypted and secure. We use Stripe to
							process payments.
						</Text>
					</Space>
				</div>

				{/* Action Buttons */}
				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<Button block onClick={onCancel} disabled={processing || loading}>
							Cancel
						</Button>
					</Col>
					<Col xs={24} sm={12}>
						<Button
							type="primary"
							htmlType="submit"
							block
							loading={processing || loading}
							disabled={!stripe}
						>
							{submitText}
						</Button>
					</Col>
				</Row>
			</Form>
		</Card>
	);
};

export const StripePaymentForm: React.FC<StripePaymentFormProps> = (props) => {
	const stripePromise = getStripe();

	return (
		<Elements stripe={stripePromise}>
			<PaymentFormContent {...props} />
		</Elements>
	);
};
